/**
 * Locale-aware formatting.
 *
 * Dates, numbers and currency are NEVER translation-catalog entries — they are
 * computed at render time from the active locale via Intl. This module is the
 * migration target for the ~427 hardcoded 'en-US' call sites still in the app.
 *
 * IMPORTANT — do not blind-replace `toLocaleDateString('en-US')` everywhere.
 * Some of those calls build API query params and OpenSearch range filters;
 * localising those silently corrupts the query. Use `toIsoDate` for anything
 * that leaves the browser, and the helpers below only for what a human reads.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LOCALE } from './locales';

const resolve = (locale) => locale || DEFAULT_LOCALE;

// Intl constructors are comparatively expensive; memoise per locale+options.
const cache = new Map();
const memo = (Ctor, locale, options) => {
  const key = `${Ctor.name}|${locale}|${JSON.stringify(options || {})}`;
  let f = cache.get(key);
  if (!f) {
    f = new Ctor(locale, options);
    cache.set(key, f);
  }
  return f;
};

export function formatDate(value, locale, options = { dateStyle: 'medium' }) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return memo(Intl.DateTimeFormat, resolve(locale), options).format(d);
}

export function formatDateTime(value, locale, options = { dateStyle: 'medium', timeStyle: 'short' }) {
  return formatDate(value, locale, options);
}

export function formatNumber(value, locale, options) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '';
  return memo(Intl.NumberFormat, resolve(locale), options).format(Number(value));
}

export function formatCurrency(value, locale, currency = 'USD', options) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '';
  return memo(Intl.NumberFormat, resolve(locale), {
    style: 'currency',
    currency,
    ...options,
  }).format(Number(value));
}

export function formatPercent(value, locale, options = { maximumFractionDigits: 1 }) {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '';
  return memo(Intl.NumberFormat, resolve(locale), { style: 'percent', ...options })
    .format(Number(value));
}

/** Compact form for stat tiles — 12.3K / 4.5M, localised (CJK uses 万/億). */
export function formatCompact(value, locale, options) {
  return formatNumber(value, locale, { notation: 'compact', maximumFractionDigits: 1, ...options });
}

const RELATIVE_UNITS = [
  ['year', 31536000000], ['month', 2592000000], ['week', 604800000],
  ['day', 86400000], ['hour', 3600000], ['minute', 60000], ['second', 1000],
];

/** "3 hours ago" / "vor 3 Stunden" — CLDR-correct in every locale. */
export function formatRelativeTime(value, locale) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const [unit, ms] = RELATIVE_UNITS.find(([, u]) => abs >= u) || ['second', 1000];
  return memo(Intl.RelativeTimeFormat, resolve(locale), { numeric: 'auto' })
    .format(Math.round(diff / ms), unit);
}

/**
 * Machine-readable YYYY-MM-DD, locale-independent. Use for API params,
 * OpenSearch ranges, export filenames — anything not read by a human.
 */
export function toIsoDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Formatters bound to the active UI locale. */
export function useFormat() {
  const { i18n } = useTranslation();
  const locale = i18n.language || DEFAULT_LOCALE;

  return {
    locale,
    formatDate: useCallback((v, o) => formatDate(v, locale, o), [locale]),
    formatDateTime: useCallback((v, o) => formatDateTime(v, locale, o), [locale]),
    formatNumber: useCallback((v, o) => formatNumber(v, locale, o), [locale]),
    formatCurrency: useCallback((v, c, o) => formatCurrency(v, locale, c, o), [locale]),
    formatPercent: useCallback((v, o) => formatPercent(v, locale, o), [locale]),
    formatCompact: useCallback((v, o) => formatCompact(v, locale, o), [locale]),
    formatRelativeTime: useCallback((v) => formatRelativeTime(v, locale), [locale]),
    toIsoDate,
  };
}
